import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Subject, takeUntil } from 'rxjs';

import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { CommonModule, NgIf } from '@angular/common';
import { AuthService } from 'app/core/auth/auth.service';
import { QueriesService } from 'app/services/queries.service';
import { environment } from 'environments/environment';
import { User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { fuseAnimations } from '@fuse/animations';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [FuseLoadingBarComponent, FuseVerticalNavigationComponent, UserComponent, MatIconModule, MatButtonModule, FuseFullscreenComponent, RouterOutlet, NgIf],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    public urlBase = environment.urlArchivos;
    navigation: Navigation;
    menu;
    user: User;
    public userType = localStorage.getItem('user-role');

    notification;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _qS: QueriesService,
        private dialog: MatDialog,

    ) {
        this.user = {
            id: this._authService.userId,
            name: this._authService.userName,
            surnames: this._authService.userSurnames,
            email: this._authService.userEmail,
            phone: this._authService.userPhone,
            avatar: this._authService.userAvatar,
            status: this._authService.userStatus,
        };
        this.user.name = this._authService.userName;
        this.user.avatar = this._authService.userAvatar;
        this.user.email = this._authService.userEmail;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        if (this.userType === 'client') {
            this.getLastUnreadNotification();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    getLastUnreadNotification(): void {
        this._qS.getDatos('latest-unread-notification').subscribe((data) => {
            this.notification = data;
            if (this.notification) {
                this.openUnreadNotificationDialog();
            }
        });
    }

    openUnreadNotificationDialog(): void {
        const dialogRef = this.dialog.open(UnreadNotificationDialogComponent, { 
            data: this.notification,
            disableClose: true // Evita que se cierre al hacer clic fuera
        });
        dialogRef.afterClosed().subscribe({
            next: () => {
                this.notification = null;
                this.getLastUnreadNotification();
            }
        });
    }
}

@Component({
    selector: 'unread-notification-modal',
    templateUrl: './unread-notification-modal.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        CommonModule,
        MatTooltipModule,
        MatCheckboxModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatDividerModule
    ]
})

export class UnreadNotificationDialogComponent implements OnInit {
    confirmChecked: FormControl = new FormControl(false);
    sanitizedMessage: SafeHtml;

    constructor(
        public dialogRef: MatDialogRef<UnreadNotificationDialogComponent>,
        private _qS: QueriesService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private sanitizer: DomSanitizer
    ) {

    }

    ngOnInit(): void {
        // Sanitizar el mensaje HTML con DOMPurify
        if (this.data.message) {
            // DOMPurify con configuración para permitir estilos inline
            const cleanHtml = DOMPurify.sanitize(this.data.message, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                               'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'span', 'div',
                               'table', 'thead', 'tbody', 'tr', 'th', 'td'],
                ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'colspan', 'rowspan'],
                ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                // Permitir propiedades CSS específicas en el atributo style
                ADD_ATTR: ['style'],
                FORBID_ATTR: [],
                FORBID_TAGS: ['script', 'iframe', 'embed', 'object'],
                // Importante: permitir estilos inline
                ALLOW_DATA_ATTR: false,
                WHOLE_DOCUMENT: false,
                RETURN_DOM: false,
                RETURN_DOM_FRAGMENT: false,
                FORCE_BODY: false
            });
            // Usar bypassSecurityTrustHtml para preservar los estilos
            // DOMPurify ya ha sanitizado, así que es seguro
            this.sanitizedMessage = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
        }
    }

    markRead(): void {
        if (this.confirmChecked.value) {
            this._qS.getDatos('mark-notification-as-read/' + this.data.id).subscribe((result) => {
                if (result.data === 'Notificación marcada como leída.'){
                    this.dialogRef.close();
                }
            });
        }
    }

    showPDF(): void {
        this._qS.getDatos('show-notification-pdf/'+this.data.id, 'blob').subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            },
            error: (error) => {
                this._qS.manageErrors(error);
            },
        });
    }
}